// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  AppIcon,
  KpiBoxes,
  OptionsSelector,
  PageContainer,
} from '@shared/ui-kit/components';
// eslint-disable-next-line @nx/enforce-module-boundaries -- temporary live testing of @client/banks
import { BankUploadButton } from '@client/banks/components/bank-upload';
import type { FC, ReactNode } from 'react';
import { useBankAccounts } from './use-bank-accounts';
import { ShareInstitutionsButton, RefreshControl, Loader } from '../../components';
import { BanksConnect } from '../banks-connect/banks-connect';
import { NuvaraInversisBanner } from '@shared/advisory/account-workflow/components';
import { BankAccountDrawer } from './bank-account-drawer';
import { BanksList } from './banks-list';
import { Typography } from '@mui/material';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { ShowInAppClient } from '@shared/ui-kit/utils/app-utils';

export const BankAccounts: FC = () => {
  const {
    t,
    dataForTable,
    kpis,
    onRemoveInstitution,
    onUnshareAccount,
    onRefresh,
    status,
    institutions,
    isSyncing,
    selectedAccount,
    openDrawerForAccount,
    drawerOpen,
    onDrawerClose,
    onDrawerOpen,
    tags,
    initialAssignedTags,
    onChangeTagIds,
    onCreateTag,
    banks,
    accountsCount,
    banksCount,
    tagsOptions,
    selectedTag,
    setSelectedTag,
    hasNuvaraInversisAccount,
  } = useBankAccounts();

  const handleShareEvent = () => undefined;

  const loaderEmptyDescription: ReactNode | undefined =
    dataForTable.length > 0 && banks.length === 0 ? (
      <Translation i18nKey="aggregator.no-bank-results" ns="aggregator" />
    ) : undefined;

  return (
    <>
      <BankAccountDrawer
        product={selectedAccount?.product}
        institution={selectedAccount?.institution}
        open={drawerOpen}
        onClose={onDrawerClose}
        onOpen={onDrawerOpen}
        onShare={handleShareEvent}
        tags={tags}
        initialAssignedTags={initialAssignedTags}
        onChangeTagIds={onChangeTagIds}
        onCreateTag={onCreateTag}
      />

      <ShowInAppClient>
        {!hasNuvaraInversisAccount && <NuvaraInversisBanner />}
      </ShowInAppClient>

      <PageContainer
        titlePage={
          <span className="flex flex-col">
            <Translation i18nKey="common.banks" ns="common" />
            <span className="text-lg font-normal text-primary-medium">
              <Translation
                i18nKey="aggregator.connected-accounts-count"
                ns="aggregator"
                values={{ count: accountsCount }}
              />
            </span>
          </span>
        }
        actionBtn={
          <div className="flex items-center gap-x-4">
            <div className="w-60">
              <OptionsSelector
                optionsAvailable={tagsOptions}
                onChangeValueSelected={setSelectedTag}
                optionSelected={selectedTag}
                defaultValue=""
                defaultText={t('aggregator.all-accounts', { ns: 'aggregator' })}
                selectType="white"
                leadingIcon="Funnel"
              />
            </div>
            <ShowInAppClient>
              <ShareInstitutionsButton institutions={institutions} />
              <BankUploadButton
                externalConnectContent={<BanksConnect variant="drawer" />}
              />
            </ShowInAppClient>
          </div>
        }
      >
        <Loader
          status={status}
          shouldDisplayContent={dataForTable.length > 0 && banks.length > 0}
          shouldDisplayEmptyState={dataForTable.length === 0 || banks.length === 0}
          emptyStateDescription={loaderEmptyDescription}
        >
          <div className="mb-4 grid grid-cols-2 gap-6 md:grid-cols-4">
            {kpis.map((kpis, idx) => (
              <KpiBoxes key={idx} {...kpis} />
            ))}
          </div>

          <div className="mb-6 mt-8 flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              <AppIcon icon="Building" size="lg" color="inherit" />
              <Typography variant="h5" className="!text-lg">
                <Translation
                  i18nKey="aggregator.banking-entities-count"
                  ns="aggregator"
                  values={{ count: banksCount }}
                />
              </Typography>
            </div>

            <RefreshControl
              onRefresh={() => onRefresh('all', 'all')}
              disabled={
                status === 'fetching' || dataForTable.length === 0 || isSyncing
              }
            />
          </div>

          <BanksList
            banks={banks}
            onAccountClick={openDrawerForAccount}
            onRemoveBank={onRemoveInstitution}
            onUnshareAccount={onUnshareAccount}
            onRefreshBank={onRefresh}
          />
        </Loader>
      </PageContainer>
    </>
  );
};
